/**
 * @file useTimerTick.ts
 * @brief Single app-wide interval that advances running timer counters once per second.
 */

import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import { selectIsTimerRunning, tick } from '@/store/slices/timerSlice';

/**
 * Registers exactly one timer tick interval for the whole application.
 * Must only be called once (from Layout) to avoid duplicate increments.
 */
export function useTimerTick(): void {
  const dispatch = useAppDispatch();
  const isRunning = useAppSelector(selectIsTimerRunning);

  useEffect(() => {
    if (!isRunning) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      dispatch(tick());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isRunning, dispatch]);
}
